import { useMutation } from '@apollo/client/react';
import { ADD_EMAILS_MUTATION } from '../../integrations/allkort/graphql/gql/EmailMutations';
import { AddEmailsResponse, EmailTypeId } from '../../integrations/allkort/graphql/interface/Emails';

/**
 * useAddEmail
 *
 * Custom hook for adding an email address to the customer's account.
 *
 * @example
 * ```tsx
 * const { addEmail, loading, error, data } = useAddEmail();
 *
 * const handleAdd = async () => {
 *   const result = await addEmail({
 *     address: 'user@example.com',
 *     customerId: currentCustomer.id,
 *     emailType: 'MAIN',
 *   });
 *   if (result) console.log('Added:', result);
 * };
 * ```
 */
export const useAddEmail = () => {
	const [addEmailsMutation, { loading, error, data }] = useMutation<AddEmailsResponse>(
		ADD_EMAILS_MUTATION
	);

	/**
	 * Add an email address to the customer's account
	 * @param address    - The email address to add
	 * @param customerId - The customer's ID (currentCustomer.id)
	 * @param emailType  - Email type enum (e.g. 'MAIN', 'INFORMATION', etc.)
	 * @param sortOrder  - Display sort order, defaults to 1
	 * @returns The added email list, or null if the mutation failed
	 */
	const addEmail = async ({
		address,
		customerId,
		emailType,
		sortOrder = 1,
	}: {
		address: string
		customerId: number
		emailType: EmailTypeId
		sortOrder?: number
	}) => {
		try {
			const result = await addEmailsMutation({
				variables: { address, customerId, emailType, sortOrder },
			});

			if (result.error) {
				const message = (result.error as any).graphQLErrors?.[0]?.message ?? result.error.message;
				console.error('[useAddEmail] GraphQL error:', message);
				throw new Error(message);
			}

			return result.data?.addEmails?.email ?? null;
		} catch (err) {
			// Re-throw so the caller can inspect and display the message
			throw err;
		}
	};

	return { addEmail, loading, error, data };
};
